/**
 * /finance/payouts — Installer Payouts
 * ──────────────────────────────────────
 * Standalone home for the installer_payouts / installer_payout_invoices
 * system (previously a small "Ready to Pay" sub-table wedged into the
 * bottom of ProjectCloseoutCockpit — see git history around 2026-08-16).
 * Queries installer_payouts directly (via InstallerPayoutsService), so
 * unlike the old closeout-embedded table this is NOT gated by closeout
 * queue membership.
 *
 * Never renders Job.financials.contractValue — installer payout figures
 * only. Customer name is shown (staff-only surface, not installer-facing).
 *
 * Incentives (e.g. a "5 Star Review" bonus) are staff-entered and reviewed
 * here — see installer_payout_incentives / installer-payout-incentive-upsert.
 * Only approved incentives count toward the invoice total, computed by
 * installer-invoice-amount-compute.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, CheckCircle2, FileText, Plus, ThumbsUp, ThumbsDown, Send } from "lucide-react";
import {
  InstallerPayoutsService,
  type ReadyToPayItem,
  type InstallerPayoutIncentiveRow,
  type InstallerPayoutIncentiveType,
} from "@/services/installerPayouts.service";
import { FinanceNavStrip } from "@/components/finance/FinanceTabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CockpitPage,
  CockpitMasthead,
  HeroMetric,
  CockpitListShell,
  CockpitColumns,
  CockpitRow,
  CockpitEmpty,
  StatusDot,
} from "@/components/cockpit/CockpitShell";

const TEMPLATE = "minmax(110px,0.9fr) minmax(140px,1.2fr) minmax(110px,0.9fr) minmax(120px,1fr) 90px 110px 90px minmax(150px,1.2fr) 130px";

function money(n: number): string {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function InstallerPayoutsPage() {
  const [items, setItems] = useState<ReadyToPayItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [markingPaidId, setMarkingPaidId] = useState<string | null>(null);
  const [sendingInvoiceId, setSendingInvoiceId] = useState<string | null>(null);
  const [detailPayoutId, setDetailPayoutId] = useState<string | null>(null);
  const bootstrappedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await InstallerPayoutsService.listReadyToPay();
      if (error) {
        toast.error("Failed to load installer payouts");
      } else {
        setItems(data);
      }
    } catch {
      toast.error("Failed to load installer payouts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    void load();
  }, [load]);

  const handleDownloadInvoice = useCallback(async (filePath: string) => {
    const { url, error } = await InstallerPayoutsService.getInvoiceDownloadUrl(filePath);
    if (error || !url) {
      toast.error("Failed to open invoice");
      return;
    }
    window.open(url, "_blank", "noopener");
  }, []);

  const handleMarkPaid = useCallback(async (payoutId: string) => {
    setMarkingPaidId(payoutId);
    const { ok, error } = await InstallerPayoutsService.markPaid(payoutId);
    setMarkingPaidId(null);
    if (!ok || error) {
      toast.error("Failed to mark payout as paid");
      return;
    }
    toast.success("Payout marked paid");
    void load();
  }, [load]);

  const handleSendInvoice = useCallback(async (item: ReadyToPayItem) => {
    setSendingInvoiceId(item.payout.id);
    const { ok, error } = await InstallerPayoutsService.sendInvoiceOnBehalf(item);
    setSendingInvoiceId(null);
    if (!ok || error) {
      toast.error("Failed to send invoice");
      return;
    }
    toast.success("Invoice sent");
    void load();
  }, [load]);

  const detailItem = useMemo(
    () => (detailPayoutId ? items.find((it) => it.payout.id === detailPayoutId) ?? null : null),
    [detailPayoutId, items],
  );

  return (
    <CockpitPage>
      <CockpitMasthead
        title="Installer Payouts"
        right={(
          <HeroMetric
            label="Awaiting remittance"
            value={items.length}
            hero
            tone={items.length > 0 ? "warn" : "default"}
          />
        )}
      />

      <FinanceNavStrip />

      <CockpitListShell>
        {loading && <div className="px-2 py-1 text-[11px] text-muted-foreground">Loading payouts...</div>}
        <CockpitColumns template={TEMPLATE}>
          <span>Job</span><span>Customer</span><span>Type</span><span>Location</span><span>Date</span><span>Payout</span><span>Status</span><span>Invoice</span><span />
        </CockpitColumns>
        {!loading && items.length === 0 ? (
          <CockpitEmpty message="No payouts are awaiting remittance." />
        ) : items.map((item) => {
          const { payout, invoice, incentives, jobDisplayRef, job } = item;
          const pendingIncentives = incentives.filter((i) => i.status === "pending").length;
          return (
            <CockpitRow key={payout.id} template={TEMPLATE} onClick={() => setDetailPayoutId(payout.id)}>
              <p className="text-sm font-medium text-foreground tabular-nums">{jobDisplayRef}</p>
              <p className="text-sm text-foreground truncate">{job?.customerName || "—"}</p>
              <p className="text-xs text-muted-foreground truncate">{job?.projectType || "—"}</p>
              <p className="text-xs text-muted-foreground truncate">
                {[job?.city, job?.state].filter(Boolean).join(", ") || "—"}
              </p>
              <p className="text-xs text-muted-foreground tabular-nums">{formatDate(job?.scheduledInstallDate)}</p>
              <div>
                <p className="text-sm text-foreground tabular-nums">{money(invoice?.total ?? payout.payout_amount ?? 0)}</p>
                {pendingIncentives > 0 && (
                  <p className="text-[10px] text-warning">
                    {pendingIncentives} incentive{pendingIncentives > 1 ? "s" : ""} pending
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <StatusDot tone={payout.status === "processing" ? "warn" : "default"} />
                <span className="text-xs text-muted-foreground capitalize">{payout.status.replace(/_/g, " ")}</span>
              </div>
              {invoice ? (
                <div className="flex items-center gap-2 min-w-0">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm text-primary hover:underline truncate"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadInvoice(invoice.file_path);
                    }}
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{invoice.invoice_number}</span>
                  </button>
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    {invoice.source_type === "auto_generated" ? "Auto-generated" : "Installer upload"}
                  </Badge>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Awaiting installer invoice</p>
              )}
              <div className="flex justify-end">
                {invoice ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[11px]"
                    disabled={markingPaidId === payout.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkPaid(payout.id);
                    }}
                  >
                    <CheckCircle2 className="mr-1 h-3 w-3" /> Mark Paid
                  </Button>
                ) : payout.status === "approved" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[11px]"
                    disabled={sendingInvoiceId === payout.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendInvoice(item);
                    }}
                  >
                    <Send className="mr-1 h-3 w-3" /> {sendingInvoiceId === payout.id ? "Sending…" : "Send Invoice"}
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" disabled>
                    <Download className="mr-1 h-3 w-3" /> No invoice yet
                  </Button>
                )}
              </div>
            </CockpitRow>
          );
        })}
      </CockpitListShell>

      {detailItem && (
        <PayoutDetailDialog
          item={detailItem}
          open={!!detailPayoutId}
          onOpenChange={(o) => !o && setDetailPayoutId(null)}
          onMutated={load}
          onDownloadInvoice={handleDownloadInvoice}
        />
      )}
    </CockpitPage>
  );
}

const INCENTIVE_TYPE_LABEL: Record<InstallerPayoutIncentiveType, string> = {
  five_star: "5 Star Review",
  other: "Other",
};

function PayoutDetailDialog({
  item, open, onOpenChange, onMutated, onDownloadInvoice,
}: {
  item: ReadyToPayItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMutated: () => void;
  onDownloadInvoice: (filePath: string) => void;
}) {
  const { payout, invoice, incentives, jobDisplayRef, job } = item;
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState<InstallerPayoutIncentiveType>("five_star");
  const [newLabel, setNewLabel] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [savingId, setSavingId] = useState<string | null>(null);

  const handleAdd = async () => {
    const amount = Number(newAmount);
    if (!(amount > 0)) {
      toast.error("Enter an amount greater than 0");
      return;
    }
    setSavingId("new");
    const { error } = await InstallerPayoutsService.createIncentive({
      payoutId: payout.id,
      type: newType,
      label: newType === "other" ? newLabel.trim() || undefined : undefined,
      amount,
    });
    setSavingId(null);
    if (error) {
      toast.error("Failed to add incentive");
      return;
    }
    toast.success("Incentive added — pending approval");
    setAdding(false);
    setNewLabel("");
    setNewAmount("");
    onMutated();
  };

  const handleDecision = async (incentive: InstallerPayoutIncentiveRow, decision: "approve" | "reject") => {
    setSavingId(incentive.id);
    const { error } = decision === "approve"
      ? await InstallerPayoutsService.approveIncentive(incentive.id)
      : await InstallerPayoutsService.rejectIncentive(incentive.id);
    setSavingId(null);
    if (error) {
      toast.error(`Failed to ${decision} incentive`);
      return;
    }
    toast.success(decision === "approve" ? "Incentive approved" : "Incentive rejected");
    onMutated();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{jobDisplayRef}</DialogTitle>
          <DialogDescription>
            {[job?.customerName, job?.projectType, [job?.city, job?.state].filter(Boolean).join(", ")]
              .filter(Boolean)
              .join(" · ") || "No job context available"}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border bg-muted/20 p-3 space-y-2 text-sm">
          <div className="flex items-center justify-between font-semibold">
            <span>Payout</span>
            <span className="tabular-nums">{money(invoice?.total ?? payout.payout_amount ?? 0)}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Status</span>
            <span className="capitalize">{payout.status.replace(/_/g, " ")}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Scheduled</span>
            <span>{formatDate(job?.scheduledInstallDate)}</span>
          </div>
          {invoice ? (
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-primary hover:underline"
              onClick={() => onDownloadInvoice(invoice.file_path)}
            >
              <FileText className="h-3 w-3" /> View invoice {invoice.invoice_number}
            </button>
          ) : (
            <p className="text-xs text-muted-foreground">Awaiting installer invoice</p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Incentives</p>
            {!adding && (
              <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setAdding(true)}>
                <Plus className="mr-1 h-3 w-3" /> Add incentive
              </Button>
            )}
          </div>

          {incentives.length === 0 && !adding && (
            <p className="text-[11px] text-muted-foreground italic">No incentives recorded for this payout.</p>
          )}

          <div className="space-y-2">
            {incentives.map((incentive) => (
              <div key={incentive.id} className="rounded-md border bg-card p-2.5 space-y-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-foreground">
                    {INCENTIVE_TYPE_LABEL[incentive.type]}
                    {incentive.label ? ` — ${incentive.label}` : ""}
                  </p>
                  <p className="text-sm font-medium tabular-nums">{money(incentive.amount)}</p>
                </div>
                <div className="flex items-center justify-between">
                  <Badge
                    variant="outline"
                    className={
                      incentive.status === "approved"
                        ? "text-[10px] text-success border-success/40"
                        : incentive.status === "rejected"
                        ? "text-[10px] text-destructive border-destructive/40"
                        : "text-[10px] text-warning border-warning/40"
                    }
                  >
                    {incentive.status}
                  </Badge>
                  {incentive.status === "pending" && (
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[11px] text-success"
                        disabled={savingId === incentive.id}
                        onClick={() => handleDecision(incentive, "approve")}
                      >
                        <ThumbsUp className="mr-1 h-3 w-3" /> Approve
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[11px] text-destructive"
                        disabled={savingId === incentive.id}
                        onClick={() => handleDecision(incentive, "reject")}
                      >
                        <ThumbsDown className="mr-1 h-3 w-3" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {adding && (
            <div className="rounded-md border bg-card p-2.5 space-y-2">
              <div className="flex gap-1 p-1 rounded-md bg-muted/60">
                <button
                  type="button"
                  className={`flex-1 h-7 text-[11px] rounded ${newType === "five_star" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                  onClick={() => setNewType("five_star")}
                >
                  5 Star Review
                </button>
                <button
                  type="button"
                  className={`flex-1 h-7 text-[11px] rounded ${newType === "other" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"}`}
                  onClick={() => setNewType("other")}
                >
                  Other
                </button>
              </div>
              {newType === "other" && (
                <Input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Describe what this incentive is for"
                  className="h-8 text-sm"
                />
              )}
              <div className="flex items-end gap-2">
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="1"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  placeholder="Amount"
                  className="h-8 text-sm"
                />
                <Button type="button" size="sm" className="h-8 text-[11px]" disabled={savingId === "new"} onClick={handleAdd}>
                  Save
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-8 text-[11px]" onClick={() => setAdding(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
